import React from 'react';
import clone from 'clone';
import {Document} from 'react-pdf';

import CustomScrollbar, {CustomScrollbarTrackVisibility} from '../../../lib/custom-scrollbar/custom-scrollbar';
import OEToolbox from '../../../lib/tools/oe-toolbox';
import retardUpdate from '../../../lib/react/update-retarder';
import OEThemeWaitingController from '../../views/oe-theme-waiting-controller';
import {OEPDFViewProps} from './oe-pdf-view-model';
import OEPDFViewPage from './oe-pdf-view-page';

export default class OEPDFView extends React.PureComponent {

    constructor(props) {
        super(props);

        //
        this.debugOutEnabled = false;

        //
        this.scrollTop = null;

        if(typeof(this.props.pageIndex) === 'number')   {
            this.deferredScrollTo = {pageIndex: this.props.pageIndex};
        } else if(typeof(this.props.page) === 'number')   {
            this.deferredScrollTo = {pageIndex: this.props.page - 1};
        }

        //this.deferredScrollTo = {pageIndex: 23};    // for testing deferred scrolling

        //
        this.initialDocumentState = {
            loaded:             false,
            numPages:           0,
            size:               {w: 0, h: 0},
            numPagesLoaded:     0,
            numPagesRendered:   0,
            pages:              []
        };

        this.documentState = clone(this.initialDocumentState);

        this.layoutSize = null;
        this.layoutSizeIsChanging = 0;

        this.pageRefs = {};

        //
        this.state = {
            documentState:      clone(this.documentState),
            layoutSize:         clone(this.layoutSize),
            layoutSizeIsChanging: !!this.layoutSizeIsChanging
        };

        //
        this.updatePageVisibilityDebounced = OEToolbox.debounce2(this.updatePageVisibility.bind(this), 10);

        //
        this.onScrollbarRef = this.onScrollbarRef.bind(this);
        this.onPageRef = this.onPageRef.bind(this);

        this.onScrollbarResize = this.onScrollbarResize.bind(this);
        this.onScroll = this.onScroll.bind(this);
        this.onScrollStop = this.onScrollStop.bind(this);

        this.onLoadSuccess = this.onLoadSuccess.bind(this);
        this.onLoadError = this.onLoadError.bind(this);
        this.onPageLoaded = this.onPageLoaded.bind(this);
        this.onPageRendered = this.onPageRendered.bind(this);
    }

    getDocumentState()  {
        return this.documentState;
    }

    getDocumentPageState(index)    {
        return index >= 0 && index < this.documentState.numPages ? this.documentState.pages[index] : undefined;
    }

    getPageRef(index)    {
        if(!this.pageRefs)  return;
        let key = 'ref-' + index.toString();
        return this.pageRefs[key];
    }

    componentWillReceiveProps(nextProps) {
        if(nextProps.fileURL != this.props.fileURL) {
            this.deferredScrollTo = null;
            let documentStateChanged = !OEToolbox.shallowEqual(this.documentState, this.initialDocumentState, true);
            this.documentState = clone(this.initialDocumentState);
            this.setState({documentState: clone(this.documentState)});
            if(documentStateChanged)    this.onDocumentStateChanged();
        }

        if(nextProps.page != this.props.page) {
            this.scrollTo(nextProps.page);
        }
    }

    componentWillUnmount()  {
        this.unmounted = true;
        this.pageRefs = {};
    }

    allPagesLoaded(toIndex)    {
        if(!this.documentState.loaded) return false;
        if(this.documentState.numPages === 0)   return true;

        if(typeof(toIndex) !== 'number')    {
            return this.documentState.numPages === this.documentState.numPagesLoaded;
        }

        toIndex = Math.min(Math.max(toIndex, 0), this.documentState.numPages - 1);
        for(let i = 0; i <= toIndex; ++i)   {
            if(!this.documentState.pages[i].loaded) return false;
        }
        
        return true;
    }

    allPagesInDOM(toIndex)    {
        if(!this.documentState.loaded) return false;
        if(this.documentState.numPages === 0)   return true;

        toIndex = typeof(toIndex) === 'number' ? Math.min(Math.max(toIndex, 0), this.documentState.numPages - 1) : (this.documentState.numPages - 1);

        for(let i = 0; i <= toIndex; ++i)   {
            if(!this.getPageRef(i) || !this.getPageRef(i).getElementRef()) return false;
        }

        return true;
    }

    allPagesRendered(toIndex)    {
        if(!this.documentState.loaded) return false;
        if(this.documentState.numPages === 0)   return true;

        if(typeof(toIndex) !== 'number')    {
            return this.documentState.numPages === this.documentState.numPagesRendered;
        }

        toIndex = Math.min(Math.max(toIndex, 0), this.documentState.numPages - 1);
        for(let i = 0; i <= toIndex; ++i)   {
            if(!this.documentState.pages[i].rendered) return false;
        }

        return true;
    }

    scrollTopForPage(index) {
        if(typeof(index) != 'number')   return;
        let scrollbarElementRef = this.scrollbar ? this.scrollbar.getElementRef() : null;
        if(!scrollbarElementRef)    return;
        let pageRef = this.getPageRef(index);
        let pageElementRef = pageRef ? pageRef.getElementRef() : null;
        if(!pageElementRef) return;

        let element = pageElementRef;
        let offsetTop = 0;
        while(element && element != scrollbarElementRef)    {
            offsetTop += element.offsetTop;
            element = element.parentElement;
        }

        return offsetTop;
    }

    allowScrolling(index)    {
        if(this.unmounted || !this.scrollbar)  return false;

        if(!this.allPagesLoaded(index) || !this.allPagesInDOM(index)) return false;

        if(typeof(index) == 'number') {
            if(!this.scrollbarSize) return false;

            let scrollTop = this.scrollTopForPage(index);
            if(scrollTop === undefined)  return false;

            if(this.scrollbarSize.h < scrollTop)    return false;
        }

        return true;
    }

    scrollTo(index, completed)    {
        let offsetTop = this.scrollTopForPage(index);

        if(offsetTop == undefined || !this.allowScrolling(index))    {
            let deferredScrollTo = {pageIndex: index, completed: completed};
            if(OEToolbox.shallowEqual(this.deferredScrollTo, deferredScrollTo)) return;
            this.deferredScrollTo = deferredScrollTo;
            this.updatePageVisibility();
            return;
        }

        this.deferredScrollTo = null;

        let scrollTop = offsetTop;
        this.scrollTop = scrollTop;

        this.scrollbar.scrollTop(scrollTop, completed);
    }

    tryDeferredScrollTo()   {
        if(!this.deferredScrollTo) return;
        let deferredScrollTo = clone(this.deferredScrollTo);
        this.scrollTo(deferredScrollTo.pageIndex, deferredScrollTo.completed);
    }

    updateDocument(pdf)    {
        this.documentState.loaded = true;
        this.documentState.numPages = pdf.numPages;
        this.documentState.pages = new Array(this.documentState.numPages);
        for(let i = 0; i < this.documentState.numPages; ++i)    {
            let page = {loaded: false, rendered: false, visible: false, shouldBeVisible: false};
            this.documentState.pages[i] = page;
        }
        this.setState({documentState: clone(this.documentState)});
        this.onDocumentStateChanged();
    }

    updateDocumentForPage(page, rendered)    {
        if(page.pageNumber > this.documentState.numPages)   return;
        let index = page.pageNumber - 1;
        let documentState = this.documentState;
        let pageState = documentState.pages[index];

        //
        let prevDocumentState = clone(documentState);

        //
        let pageSet = !pageState.loaded;
        if(pageSet) {
            pageState.loaded = true;

            if(index == 0)  {
                documentState.size = {w: page.originalWidth, h: page.originalHeight};
            }

            documentState.numPagesLoaded++;
        }

        //
        if(rendered && !pageState.rendered) {
            pageState.rendered = true;

            documentState.numPagesRendered++;
        }

        //
        if(pageSet && index == 0) this.updateLayoutSize();

        //
        if(OEToolbox.shallowEqual(documentState, prevDocumentState, true))    return;
        
        //
        this.setState({documentState: clone(this.documentState)});

        //
        this.onDocumentPageStateChanged(index, prevDocumentState);
    }

    determineLayoutSize()   {
        if(!this.scrollbarSize || this.scrollbarSize.w === 0)  return;

        let height = this.scrollbarSize.w * 1296 / 1920.0;

        if(this.documentState.numPages && this.documentState.numPagesLoaded && this.documentState.size.w)    {
            height = this.scrollbarSize.w * this.documentState.size.h / this.documentState.size.w;
        }

        height *= 1.05;

        return {w: this.scrollbarSize.w, h: height}
    }

    updateLayoutSize()  {
        let layoutSize = this.determineLayoutSize();
        if(!layoutSize || OEToolbox.shallowEqual(layoutSize, this.layoutSize)) return;
        let prevLayoutSize = clone(this.layoutSize);
        this.layoutSize = layoutSize;

        retardUpdate(this, () => {
            this.setState({layoutSize: clone(this.layoutSize)});
            this.onLayoutSizeChanged(prevLayoutSize);
        });
    }

    updatePageVisibility(pageIndex)  {
        if(!this.documentState.loaded || !this.documentState.numPages || !this.scrollbar || !this.scrollbar.getElementRef())  return;
        let scrollRect = this.scrollbar.getElementRef().getBoundingClientRect();

        //
        let updateShouldBeVisible = function(i)  {
            let shouldBeVisible = false;
            let page = this.documentState.pages[i];

            if(page.loaded && !this.deferredScrollTo)   {
                let pageRef = this.getPageRef(i);
                let pageElementRef = pageRef ? pageRef.getElementRef() : null;

                if(pageElementRef)    {
                    let pageRect = pageElementRef.getBoundingClientRect();
                    shouldBeVisible = !!scrollRect.width && !!scrollRect.height && !(pageRect.bottom < scrollRect.top || pageRect.top > scrollRect.bottom || pageRect.right < scrollRect.left || pageRect.left > scrollRect.right);
                }

            } else if(this.deferredScrollTo) {
                shouldBeVisible = i == this.deferredScrollTo.pageIndex;
            }
            
            if(page.shouldBeVisible === shouldBeVisible) return false;

            page.shouldBeVisible = shouldBeVisible;

            this.debugOut('PDFView - Page ' + i.toString() + ' - shouldBeVisible: ' + String(shouldBeVisible));

            return true;
        }.bind(this);

        //
        let updateVisibility = function(i)  {
            let visible = false;
            let start = Math.max(i - 1, 0);
            let end = Math.min(i + 2, this.documentState.numPages);

            for(let j = start; j < end; ++j)    {
                if(!this.documentState.pages[j].shouldBeVisible) continue;
                visible = true;
                break;
            }

            if(this.documentState.pages[i].visible === visible)  return false;

            this.documentState.pages[i].visible = visible;

            this.debugOut('PDFView - Page ' + i.toString() + ' - visible: ' + String(visible));

            return true;
        }.bind(this);

        //
        let shouldBeVisibleChanged = false;
        let visibilityChanged = false;

        if(typeof(pageIndex) === 'number')   {
            shouldBeVisibleChanged = updateShouldBeVisible(pageIndex);

            let start = Math.max(pageIndex - 1, 0);
            let end = Math.min(pageIndex + 2, this.documentState.numPages);

            for(let i = start; i < end; ++i)    {
                if(updateVisibility(i)) visibilityChanged = true;
            }

        } else {
            for(let i = 0; i < this.documentState.numPages; ++i)    if(updateShouldBeVisible(i))    shouldBeVisibleChanged = true;
            for(let i = 0; i < this.documentState.numPages; ++i)    if(updateVisibility(i)) visibilityChanged = true;
        }

        //
        if(shouldBeVisibleChanged && this.debugOutEnabled || this.props.debugOutEnabled)  {
            let pages = '';
            this.documentState.pages.forEach((page, index) => {
                if(!page.shouldBeVisible)   return;
                pages += (pages ? ', ' : '') + index;
            });
            this.debugOut('PDFView - page should be visible changed - ' + pages);
        }

        //
        if(!visibilityChanged)    return;

        //
        if(this.debugOutEnabled || this.props.debugOutEnabled)  {
            let pages = '';
            this.documentState.pages.forEach((page, index) => {
                if(!page.visible)   return;
                pages += (pages ? ', ' : '') + index;
            });
            this.debugOut('PDFView - page visibilty changed - ' + pages);
        }

        //
        this.setState({documentState: clone(this.documentState)});
    }

    setLayoutSizeChanging(enabled)  {
        if(this.unmounted)  return;

        let prevValue = this.layoutSizeIsChanging;
        this.layoutSizeIsChanging += enabled ? 1 : -1;

        if(!!prevValue == !!this.layoutSizeIsChanging)    return;

        this.debugOut('PDFView - layoutSizeIsChanging: ' + String(!!this.layoutSizeIsChanging));

        this.setState({layoutSizeIsChanging: !!this.layoutSizeIsChanging});
    }

    onDocumentStateChanged()    {
        if(this.props.onDocumentStateChanged)  this.props.onDocumentStateChanged(this, this.documentState);
    }

    onDocumentPageStateChanged(pageIndex, prevDocumentState)    {
        if(this.props.onDocumentPageStateChanged)  this.props.onDocumentPageStateChanged(this, pageIndex, this.documentState, prevDocumentState);
        if(!OEToolbox.shallowEqual(prevDocumentState.size, this.documentState.size))    this.updateLayoutSize();
        this.updatePageVisibility(pageIndex);
    }

    onLayoutSizeChanged()   {
        this.debugOut('PDFView - onLayoutSizeChanged: ' + (!this.layoutSize ? 'null' : '(' + this.layoutSize.w + ', ' + this.layoutSize.h + ')'));
        this.setLayoutSizeChanging(true);
        setTimeout(() => { this.setLayoutSizeChanging(false); }, 100);
    }

    onScrollbarRef(ref) {
        if(this.scrollbar === ref)   return;

        this.scrollbar = ref;
        if(!this.scrollbar)   return;

        this.scrollTop = this.scrollbar.getScrollTop();

        this.updateLayoutSize();
        this.updatePageVisibility();
        this.tryDeferredScrollTo();
    }

    onPageRef(ref, index)  {
        if(this.unmounted)  return;

        let key = 'ref-' + index.toString();
        if(this.pageRefs[key] == ref) return;

        this.pageRefs[key] = ref;

        if(!ref)    return;

        this.updatePageVisibilityDebounced(index);
        this.tryDeferredScrollTo();
    }

    onScrollbarResize(sender, size) {
        if(OEToolbox.shallowEqual(size, this.scrollbarSize)) return;

        let scrollTopScale = this.scrollbarSize && this.scrollbarSize.w > 0 ? size.w / this.scrollbarSize.w : 1;
        this.scrollbarSize = size;

        this.updateLayoutSize();
        this.updatePageVisibility();

        if(this.scrollTop && this.scrollbar && scrollTopScale != 1 && !this.deferredScrollTo)  {
            let scrollTop = scrollTopScale * this.scrollTop;
            this.scrollbar.scrollTop(scrollTop);
        }
    }

    renderPages()   {
        const options = this.props.options;
        const doc = this.state.documentState;

        return doc.pages.map((page, i) =>  {
            let pageNumber = i + 1;
            let onPageRef = (ref) => { this.onPageRef(ref, i); };

            return (
                <OEPDFViewPage
                    key={i}
                    debugOutEnabled={this.debugOutEnabled || this.props.debugOutEnabled}
                    className={pageNumber === doc.numPages ? 'last' : ''}
                    pageNumber={pageNumber}
                    visible={!options.dynamic || (page.loaded && page.visible)}
                    layoutWidth={this.state.layoutSize ? this.state.layoutSize.w : undefined}
                    scaleMode={options.scaleMode}
                    layoutSizeIsChanging={this.state.layoutSizeIsChanging}
                    onLoaded={this.onPageLoaded}
                    onRendered={this.onPageRendered}
                    ref={onPageRef}
                />
            );
        });
    }

    render() {
        const doc = this.state.documentState;

        //
        let style = this.props.layoutToPageHeight && this.state.layoutSize ? {height: this.state.layoutSize.h} : undefined;

        //
        let numPagesReady = this.props.options.dynamic ? this.state.documentState.numPagesLoaded : this.state.documentState.numPagesRendered;
        let showWaitingSpinner = !doc.loaded || (doc.numPages > 0 && numPagesReady < doc.numPages);
        let progress = !doc.loaded ? 0 : (doc.numPages > 0 ? numPagesReady / doc.numPages : 1);

        //
        return (
            <div className={'pdf-view ' + this.props.className}>
                
                <CustomScrollbar
                    style={style}
                    horizontalTrackVisiblity={CustomScrollbarTrackVisibility.none}
                    verticalTrackVisiblity={CustomScrollbarTrackVisibility.always}
                    onScroll={this.onScroll}
                    onScrollStop={this.onScrollStop}
                    onContentResize={this.onScrollbarResize}
                    ref={this.onScrollbarRef}
                >

                    <Document
                        file={this.props.fileURL}
                        loading=""
                        error={this.props.errorStr}
                        noData=""
                        onLoadSuccess={this.onLoadSuccess}
                        onLoadError={this.onLoadError}
                    >
                        {this.renderPages()}
                    </Document>

                </CustomScrollbar>

                <OEThemeWaitingController show={showWaitingSpinner} progress={progress} />

            </div>
        );
    }

    onScroll(sender) {
        //console.log('onScroll');
        if(this.scrollbar)   this.scrollTop = this.scrollbar.getScrollTop();
        this.updatePageVisibilityDebounced();
    }

    onScrollStop(sender) {
        console.log('onScrollStop');
        if(this.scrollbar)   this.scrollTop = this.scrollbar.getScrollTop();
        this.updatePageVisibility();
    }

    onLoadSuccess(pdf)  {
        this.updateDocument(pdf);
    }

    onLoadError(error)  {
        if(this.props.onError)  this.props.onError(this);
    }

    onPageLoaded(sender, page)    {
        this.updateDocumentForPage(page);
        this.tryDeferredScrollTo(); // element of page already in DOM and appropriate sized
    }

    onPageRendered(sender, page)   {
        this.updateDocumentForPage(page, true);
        this.tryDeferredScrollTo(); // element of page already in DOM and appropriate sized
    }

    debugOut(str)   {
        if(this.debugOutEnabled || this.props.debugOutEnabled)   console.log(str);
    }
}

OEPDFView.defaultProps = OEPDFViewProps.defaultProps;

OEPDFView.propTypes = OEPDFViewProps.propTypes;